import { useState } from 'react';

import {
  Location,
  LocationService,
  NewService,
  RestaurantService,
  Service,
  ServiceBaselineDate,
} from '../../../interfaces';
import { Button } from '@material-tailwind/react';
import { MdCreate } from 'react-icons/md';
import LocationServiceInputCard from '../InputComponents/LocationServiceInputCard';
import { Waypoint } from 'react-waypoint';

/**
 * To get an array of new service(s) where all the placeholder IDs are removed
 * @param arrNewServiceWithPlaceholderIds - Array of service to loop through to delete the placeholder IDs
 * @returns arrNewService - Array of new service(s) where all the placeholder IDs have been removed
 */
const getArrNewService = (arrNewServiceWithPlaceholderIds: Array<Service>): Array<NewService> => {
  const arrNewService = JSON.parse(JSON.stringify(arrNewServiceWithPlaceholderIds));
  for (let newServiceIndex = 0; newServiceIndex < arrNewService.length; newServiceIndex++) {
    let service = arrNewService[newServiceIndex];
    service.serviceId = undefined;
    service.menu.menuId = undefined;
    service.menuId = undefined;
    service.arrServiceBaselineDate = service.arrServiceBaselineDate.map(
      (serviceBaselineDate: ServiceBaselineDate) => ({
        startDate: serviceBaselineDate.startDate,
        endDate: serviceBaselineDate.endDate,
      })
    );
  }
  return arrNewService;
};

/**
 * Loop through array to delete placeholder menuuId for existing service(s) where its menu is to be changed to a new one
 * @param arrEditedService - Array of edited service to loop through to the placeholder menuId
 * @returns arrEditedService - Array of edited service(s) where the placeholder menuId has been removed
 */
const removeMenuIdFromArrEditedServiceWithNewMenu = (
  arrEditedServiceWithPlaceholderMenuId: Array<Service>
): Array<NewService> => {
  const arrEditedService = JSON.parse(JSON.stringify(arrEditedServiceWithPlaceholderMenuId));
  for (
    let editedServiceIndex = 0;
    editedServiceIndex < arrEditedService.length;
    editedServiceIndex++
  ) {
    if (arrEditedService[editedServiceIndex].menu.menuId === -1) {
      arrEditedService[editedServiceIndex].menu.menuId = undefined;
    }
  }
  return arrEditedService;
};

const EditLocationInputCard = (props: {
  arrRestaurantService: Array<RestaurantService>;
  locationService: LocationService;
  arrLocationService: Array<LocationService>;
  companyId: number;
  restaurantId: number;
  saveChangesToExistingCompanyService: any;
  setAlert?: (body: string, header: string) => void;
}) => {
  const [editedLocation, setEditedLocation] = useState<Location | null>(null);
  const [mapEditedServiceByServiceId, setMapEditedServiceByServiceId] = useState(new Map());
  const [mapNewServiceByServiceId, setMapNewServiceByServiceId] = useState(new Map());
  const [isButtonFloating, setIsButtonFloating] = useState<boolean>(false);

  /**
   * Function can add the edited location to map, with the locationId as the key, or remove the key-value if there is no change
   * to the location
   * Note: 'locationId' is not used in this function because there can only be editing of 1 location in this component. However this function is structured
   * to take in the same set of parameters as the props of LocationServiceInputCard component so as to reduce the number of props for the LocationServiceInputCard component.
   * @param idParameters - Object containing locationId of the edited location
   * @param editedLocation - Location object containing the edited attribute of an existing location, or undefined if the location is to be removed
   */
  const updateEditedLocation = (
    idParameters: { locationId: number },
    editedLocation?: Location
  ) => {
    if (editedLocation) {
      setEditedLocation(editedLocation);
    } else {
      setEditedLocation(null);
    }
  };

  /**
   * Function can add the edited service to map, with the serviceId as the key, or remove the key-value if there is no change
   * to the service
   * @param idParameters - Object containing serviceId of the edited service
   * @param editedService - Service object containing the edited attribute of an existing service, or undefined if the service is to be removed
   */
  const updateMapEditedServiceByServiceId = (
    idParameters: { serviceId: number },
    editedService?: Service
  ) => {
    const { serviceId } = idParameters;
    const updatedMapEditedServiceByServiceId = new Map(mapEditedServiceByServiceId);
    if (editedService) {
      updatedMapEditedServiceByServiceId.set(serviceId, editedService);
    } else {
      updatedMapEditedServiceByServiceId.delete(serviceId);
    }
    setMapEditedServiceByServiceId(updatedMapEditedServiceByServiceId);
  };

  /**
   * Function can add the new service (or when the new service is edited) to map, with the serviceId as the key, or remove the key-value if the service is to be removed
   * @param idParameters - Object containing serviceId of the new service
   * @param newService - Service object containing the new service, or undefined if the service is to be removed
   */
  const updateMapNewServiceByServiceId = (
    idParameters: { serviceId: number },
    newService?: Service
  ) => {
    const { serviceId } = idParameters;
    const updatedMapNewServiceByServiceId = new Map(mapNewServiceByServiceId);
    if (newService) {
      updatedMapNewServiceByServiceId.set(serviceId, newService);
    } else {
      updatedMapNewServiceByServiceId.delete(serviceId);
    }
    setMapNewServiceByServiceId(updatedMapNewServiceByServiceId);
  };

  /**
   * Function is run by save changes button, and calls saveChangesToExistingCompanyService function that is passed down from parent CompanyCard component.
   * Function saves edited location (if any), edited services (if any) and newly added services (if any).
   */
  const saveChangesForUpdateLocationServiceOnClick = async () => {
    // If there has been an update of restaurant id on a location, perform a check to see if there is a duplicate in new restaurant
    if (editedLocation !== null && editedLocation.restaurantId !== props.restaurantId) {
      const restaurantToBeUpdatedTo = props.arrRestaurantService.find(
        (restaurantService) => restaurantService.restaurantId === editedLocation.restaurantId
      );
      if (
        props.setAlert &&
        restaurantToBeUpdatedTo?.arrLocationService.some(
          (locationService) => locationService.name === editedLocation.name
        )
      ) {
        props.setAlert('Duplicate location name found in property to be moved to!', 'Save Error!');
        return;
      }
    }

    const arrNewService = getArrNewService(Array.from(mapNewServiceByServiceId.values()));
    // If the edited service is to be updated to a new (non-existing) menu, its menu.menuId will have to be set to undefined
    // for
    // 1. Backend - comparison of edited service's menuId and edited service's menu.menuId to register a change in menu,
    // 2. Database-Middleware - edited service's menu.menuId needs to be undefined to trigger a creation of menu
    const arrEditedService = removeMenuIdFromArrEditedServiceWithNewMenu(
      Array.from(mapEditedServiceByServiceId.values())
    );
    const isSaved = await props.saveChangesToExistingCompanyService(
      {
        companyId: props.companyId,
        restaurantId: props.restaurantId,
        locationId: props.locationService.locationId,
      },
      {
        arrServiceToBeCreated: arrNewService.length !== 0 ? arrNewService : undefined,
      },
      {
        locationToBeUpdated: editedLocation !== null ? editedLocation : undefined,
        arrServiceToBeUpdated: arrEditedService.length !== 0 ? arrEditedService : undefined,
      }
    );
    // Reset changes if the changes have been added/updated successfully
    if (isSaved) {
      setEditedLocation(null);
      setMapEditedServiceByServiceId(new Map());
      setMapNewServiceByServiceId(new Map());
    }
  };

  return (
    <div className="flex flex-col">
      <div className={`${isButtonFloating && 'fixed top-24 z-10'} self-end mx-2`}>
        <Button
          onClick={saveChangesForUpdateLocationServiceOnClick}
          size="sm"
          color="green"
          className={`SaveChangesButton bg-blue-600 w-fit self-end flex gap-x-1 pt-2 p-2 font-medium h-fit`}
        >
          <MdCreate className="self-center" />
          Save Changes
        </Button>
      </div>
      <Waypoint
        onEnter={() => setIsButtonFloating(false)}
        onLeave={() => setIsButtonFloating(true)}
      />
      <LocationServiceInputCard
        key={props.locationService.locationId}
        arrEditedService={Array.from(mapEditedServiceByServiceId.values())}
        arrLocationService={props.arrLocationService}
        arrNewLocation={[]}
        arrNewService={Array.from(mapNewServiceByServiceId.values())}
        companyId={props.companyId}
        locationId={props.locationService.locationId}
        locationService={props.locationService}
        restaurantId={props.restaurantId}
        updateEditedOrNewLocation={updateEditedLocation}
        updateMapEditedServiceByServiceId={updateMapEditedServiceByServiceId}
        updateMapForNewService={updateMapNewServiceByServiceId}
        arrRestaurantService={props.arrRestaurantService}
      />
    </div>
  );
};

export default EditLocationInputCard;
